import React, { useEffect, useState, useCallback } from "react";
import AppShell from "../components/AppShell";
import api from "../lib/api";
import { LogoQuilla } from "../components/Logo";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Buildings, Plus, Wrench, Key } from "@phosphor-icons/react";

const STATUS_TONE = {
  ACTIVA: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PROTEGIDA: "bg-amber-50 text-amber-700 border-amber-200",
  SUSPENDIDA: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Consola() {
  const { logout, session } = useAuth();
  const [hotels, setHotels] = useState([]);
  const [newHotel, setNewHotel] = useState({ name: "", license: "", plan: "Boutique", avg_rate: 100, latitude: "", longitude: "", initial_pin: "2024" });
  const [showNew, setShowNew] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [pinForm, setPinForm] = useState({ current_pin: "", new_pin: "", confirm_pin: "" });
  const [changingPin, setChangingPin] = useState(false);

  const load = useCallback(() => api.get("/consola/hotels").then(r => setHotels(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const changePin = async () => {
    if (pinForm.new_pin !== pinForm.confirm_pin) {
      toast.error("El nuevo PIN y la confirmación no coinciden");
      return;
    }
    if (!/^\d{4,6}$/.test(pinForm.new_pin)) {
      toast.error("El PIN debe tener 4 a 6 dígitos");
      return;
    }
    setChangingPin(true);
    try {
      await api.post("/consola/change-pin", { current_pin: pinForm.current_pin, new_pin: pinForm.new_pin });
      toast.success("PIN actualizado. Úsalo la próxima vez que entres.");
      setShowPin(false);
      setPinForm({ current_pin: "", new_pin: "", confirm_pin: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar el PIN");
    } finally { setChangingPin(false); }
  };

  const create = async () => {
    try {
      const payload = { ...newHotel, avg_rate: parseFloat(newHotel.avg_rate) || 0,
                        latitude: parseFloat(newHotel.latitude) || null,
                        longitude: parseFloat(newHotel.longitude) || null };
      const { data } = await api.post("/consola/hotels", payload);
      toast.success(`Hotel creado: ${data.hotel.name}. PIN gerencia inicial: ${data.gerencia_user.pin}`);
      setShowNew(false);
      setNewHotel({ name: "", license: "", plan: "Boutique", avg_rate: 100, latitude: "", longitude: "", initial_pin: "2024" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/consola/hotels/${id}`, { license_status: status });
      toast.success(`Licencia → ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="min-h-screen bg-[color:var(--q-bg)] q-safe-bottom">
      <header className="bg-white border-b border-[color:var(--q-border)] q-safe-top">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-4">
          <LogoQuilla variant="color" size={30}/>
          <div className="text-xs text-[color:var(--q-muted)] border-l border-[color:var(--q-border)] pl-3">CONSOLA DE PLATAFORMA</div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-sm">{session?.user?.name}</div>
            <button onClick={() => setShowPin(true)} data-testid="change-admin-pin-btn"
                    className="q-btn-ghost flex items-center gap-1.5 text-sm">
              <Key size={14} weight="bold"/> Cambiar mi PIN
            </button>
            <button onClick={logout} className="q-btn-ghost">Salir</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-4xl mb-2">Licencias y hoteles</h1>
            <p className="text-sm text-[color:var(--q-muted)] max-w-xl">
              Vista agregada de plataforma. Los reportes operativos, fotos, notas y aprobaciones de cada hotel <span className="font-semibold">no son accesibles</span> desde aquí — es aislamiento arquitectónico, no solo UI.
            </p>
          </div>
          <button data-testid="new-hotel-btn" onClick={() => setShowNew(true)} className="q-btn-primary flex items-center gap-2">
            <Plus size={16} weight="bold"/> Nuevo hotel
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {hotels.map(h => (
            <div key={h.id} className="q-card p-5" data-testid={`hotel-${h.license}`}>
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{background: "#1F3864", color: "#C9A227"}}>
                  <Buildings size={20} weight="fill"/>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">{h.name}</h3>
                  <div className="q-label text-[color:var(--q-muted)] font-mono">{h.license}</div>
                </div>
                <select value={h.license_status} onChange={(e) => changeStatus(h.id, e.target.value)}
                        data-testid={`status-select-${h.license}`}
                        className={`chip border ${STATUS_TONE[h.license_status]} font-semibold`}>
                  <option>ACTIVA</option><option>PROTEGIDA</option><option>SUSPENDIDA</option>
                </select>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                <div><div className="q-label text-[color:var(--q-muted)]">Plan</div><div className="font-semibold">{h.plan}</div></div>
                <div>
                  <div className="q-label text-[color:var(--q-muted)]">Health Index</div>
                  <div className="font-mono font-semibold text-lg" style={{color: h.risk_level === "bajo" ? "#10b981" : h.risk_level === "medio" ? "#C9A227" : "#ef4444"}}>{h.health_index}</div>
                </div>
                <div><div className="q-label text-[color:var(--q-muted)]">Usuarios</div><div className="font-mono font-semibold">{h.users_count}</div></div>
              </div>
            </div>
          ))}
        </div>

        {showNew && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
            <div className="q-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl mb-4">Emitir nueva licencia</h3>
              <p className="text-xs text-[color:var(--q-muted)] mb-4">El hotel nace vacío. Solo un usuario gerencia inicial con el PIN que asignes.</p>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2">
                  <label className="q-label block mb-1">Nombre del hotel</label>
                  <input data-testid="new-hotel-name" value={newHotel.name} onChange={(e) => setNewHotel({...newHotel, name: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)]"/>
                </div>
                <div>
                  <label className="q-label block mb-1">Licencia</label>
                  <input data-testid="new-hotel-license" value={newHotel.license} onChange={(e) => setNewHotel({...newHotel, license: e.target.value})} placeholder="QF-XXXXX-26" className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono uppercase"/>
                </div>
                <div>
                  <label className="q-label block mb-1">Plan</label>
                  <select value={newHotel.plan} onChange={(e) => setNewHotel({...newHotel, plan: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)]">
                    <option>Boutique</option><option>Resort</option><option>Enterprise</option>
                  </select>
                </div>
                <div>
                  <label className="q-label block mb-1">Tarifa promedio (USD/noche)</label>
                  <input type="number" value={newHotel.avg_rate} onChange={(e) => setNewHotel({...newHotel, avg_rate: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono"/>
                </div>
                <div>
                  <label className="q-label block mb-1">PIN gerencia inicial</label>
                  <input value={newHotel.initial_pin} onChange={(e) => setNewHotel({...newHotel, initial_pin: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono"/>
                </div>
                <div>
                  <label className="q-label block mb-1">Latitud (opcional)</label>
                  <input value={newHotel.latitude} onChange={(e) => setNewHotel({...newHotel, latitude: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono"/>
                </div>
                <div>
                  <label className="q-label block mb-1">Longitud (opcional)</label>
                  <input value={newHotel.longitude} onChange={(e) => setNewHotel({...newHotel, longitude: e.target.value})} className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono"/>
                </div>
              </div>
              <div className="flex gap-2 justify-end mt-6">
                <button className="q-btn-ghost" onClick={() => setShowNew(false)}>Cancelar</button>
                <button data-testid="create-hotel-submit" className="q-btn-primary" onClick={create}>Emitir licencia</button>
              </div>
            </div>
          </div>
        )}
        {showPin && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowPin(false)}>
            <div className="q-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="change-pin-modal">
              <h3 className="text-xl mb-2">Cambiar mi PIN</h3>
              <p className="text-xs text-[color:var(--q-muted)] mb-4">
                Solo tu PIN de acceso a la Consola QUILLA. La licencia (QUILLA-2026) no cambia.
              </p>
              <div className="grid gap-3 text-sm">
                <div>
                  <label className="q-label block mb-1">PIN actual</label>
                  <input data-testid="current-pin-input" type="password" inputMode="numeric" autoComplete="current-password"
                         value={pinForm.current_pin} onChange={(e) => setPinForm({...pinForm, current_pin: e.target.value})}
                         className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono tracking-widest"/>
                </div>
                <div>
                  <label className="q-label block mb-1">Nuevo PIN (4-6 dígitos)</label>
                  <input data-testid="new-pin-input" type="password" inputMode="numeric" autoComplete="new-password"
                         value={pinForm.new_pin} onChange={(e) => setPinForm({...pinForm, new_pin: e.target.value})}
                         className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono tracking-widest"/>
                </div>
                <div>
                  <label className="q-label block mb-1">Confirmar nuevo PIN</label>
                  <input data-testid="confirm-pin-input" type="password" inputMode="numeric" autoComplete="new-password"
                         value={pinForm.confirm_pin} onChange={(e) => setPinForm({...pinForm, confirm_pin: e.target.value})}
                         className="w-full h-10 px-3 rounded-lg border border-[color:var(--q-border)] font-mono tracking-widest"/>
                </div>
              </div>
              <div className="flex gap-2 justify-end mt-6">
                <button className="q-btn-ghost" onClick={() => setShowPin(false)}>Cancelar</button>
                <button data-testid="submit-change-pin-btn" className="q-btn-primary" onClick={changePin}
                        disabled={changingPin || !pinForm.current_pin || !pinForm.new_pin || !pinForm.confirm_pin}>
                  {changingPin ? "Guardando…" : "Guardar PIN"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
